import { mdiChevronDown, mdiChevronLeft, mdiChevronRight, mdiClose, mdiContentCopy, mdiOpenInNew } from '@mdi/js';
import Icon from '@mdi/react';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ProviderWorkerReportClaimDetails } from '../../api/server/models/ProviderWorkerReportClaimDetails';
import { formatDate } from '../../services/primitives/date-utils';
import { ToastContext } from '../../services/toast/toast';
import { WorkflowClaim } from '../../services/worker-report/worker-report-api';
import {
    labelOf,
    normalizeWorkflow,
    titleCase,
    weekLabel,
    weekRange,
    WORKFLOW_LABELS,
} from '../../services/worker-report/worker-report.model';
import { WorkerReportStore } from '../../stores/worker-report/worker-report.store';
import { BaseButton } from '../base/button/button';
import { DetailField } from '../base/detail-panel/detail-panel';
import { Notice } from '../base/notice/notice';
import { Spinner } from '../base/spinner/spinner';
import { TypeCountBadge, TypeFilterBar, TypeFilterChip } from '../base/type-filter-bar/type-filter-bar';
import { SidePanel } from '../messaging/side-panel';
import { formatCurrency } from '../patient-statement/utils/statement-format.utils';
import * as S from './workflow-drawer.styles';

const DAY = 'MMM d yyyy';
const ABSENT = '—';

const money = (amount: { amount?: string } | undefined): string | undefined =>
    amount?.amount !== undefined && amount.amount !== null ? formatCurrency(Number(amount.amount).toFixed(2)) : undefined;

const day = (value: string | undefined): string | undefined => (value ? formatDate(value, DAY) : undefined);

const patientName = (claim: WorkflowClaim): string | undefined =>
    [claim.patientFirst, claim.patientLast].filter(Boolean).join(' ') || undefined;

const dosRange = (claim: WorkflowClaim): string | undefined => {
    const from = day(claim.fromDos);
    const through = day(claim.throughDos);

    return from && through && through !== from ? `${from} – ${through}` : from;
};

/** `Sep 25 2026 · 12d ago`, aged against the drawer's own snapshot rather than the browser clock. */
const stateChanged = (actionDate: string | undefined, generatedAt: string | undefined): string | undefined => {
    if (!actionDate) {
        return undefined;
    }
    const when = day(actionDate);
    if (!generatedAt) {
        return when;
    }
    try {
        const days = differenceInCalendarDays(parseISO(generatedAt), parseISO(actionDate));
        return `${when} · ${days}d ago`;
    } catch {
        return when;
    }
};

interface IClaimRowProps {
    store: WorkerReportStore;
    claimId: string;
    claim: WorkflowClaim | undefined;
    detail: ProviderWorkerReportClaimDetails | undefined;
    claimHref: (claimId: string) => string;
    onCopy: (text: string, what: string) => void;
}

const ClaimRow: React.FC<IClaimRowProps> = observer(({ store, claimId, claim, detail, claimHref, onCopy }) => {
    const open = store.openClaimId === claimId;
    // Still loading is not the same as not returned: only say "unavailable" once the claims are in (NS-3).
    const unavailable = !claim && !store.claimsLoading;
    const tpa = claim?.primaryThirdPartyAdministratorName;

    return (
        <>
            <S.Row variant="unstyled" open={open} aria-label={`${open ? 'Collapse' : 'Expand'} claim ${claimId}`} onClick={(): void => store.toggleClaim(claimId)}>
                <S.Chevron>
                    <Icon path={open ? mdiChevronDown : mdiChevronRight} size={0.6} />
                </S.Chevron>
                <S.Stack>
                    <S.ClaimId>{claimId}</S.ClaimId>
                    {claim ? (
                        <>
                            <S.Strong>{patientName(claim) || ABSENT}</S.Strong>
                            {claim.accountNumber && <span>Acct {claim.accountNumber}</span>}
                        </>
                    ) : (
                        <S.Ellipsis>{unavailable ? 'Claim details unavailable' : 'Loading…'}</S.Ellipsis>
                    )}
                </S.Stack>
                <S.Stack>
                    <S.Ellipsis title={claim?.primaryPayerName}>{claim?.primaryPayerName || (claim ? ABSENT : '')}</S.Ellipsis>
                    {tpa && <S.Ellipsis title={tpa}>TPA {tpa}</S.Ellipsis>}
                </S.Stack>
                <S.Stack align="right">
                    <S.Strong>{claim ? money(claim.chargeAmount) || ABSENT : ''}</S.Strong>
                    <span>{claim ? day(claim.fromDos) || ABSENT : ''}</span>
                </S.Stack>
            </S.Row>
            {open && (
                <S.Expanded>
                    {unavailable && (
                        <S.Muted>
                            The claims list did not return this claim under any claim type, so only its workflow state
                            is shown. It may have closed or moved since the snapshot.
                        </S.Muted>
                    )}
                    <S.Fields>
                        <DetailField label="Current Action" value={detail && labelOf(store.catalog.actions, detail.workflowAction)} absent={ABSENT} />
                        <DetailField label="Step" value={detail && labelOf(store.catalog.steps, detail.workflowStep)} absent={ABSENT} />
                        <DetailField label="Workflow" value={detail && WORKFLOW_LABELS[normalizeWorkflow(detail.workflow)]} absent={ABSENT} />
                        <DetailField label="State Changed" value={stateChanged(detail?.actionDate, store.pageGeneratedAt)} absent={ABSENT} />
                        {claim && (
                            <>
                                <DetailField label="DOS" value={dosRange(claim)} absent={ABSENT} />
                                <DetailField label="DOB" value={day(claim.patientDob)} absent={ABSENT} />
                                <DetailField label="MRN" value={claim.patientExternalId} absent={ABSENT} />
                                <DetailField label="Member ID" value={claim.primaryPayerSubscriberMemberId} absent={ABSENT} />
                                <DetailField label="Secondary Payer" value={claim.secondaryPayerName} absent={ABSENT} />
                                <DetailField label="Type" value={titleCase(claim.type)} absent={ABSENT} />
                                <DetailField label="Claim Resp." value={titleCase(claim.claimResponsibilityType)} absent={ABSENT} />
                                <DetailField label="Resp. Category" value={titleCase(claim.claimResponsibilityCategoryType)} absent={ABSENT} />
                                <DetailField label="Collection Status" value={titleCase(claim.collectionStatus)} absent={ABSENT} />
                                <DetailField label="Adjudicated" value={day(claim.claimAdjudicationDate)} absent={ABSENT} />
                                <DetailField label="Payments" value={claim.payments !== undefined ? String(claim.payments) : undefined} absent={ABSENT} />
                                <DetailField label="Paperwork" value={claim.paperwork === undefined ? undefined : claim.paperwork ? 'Required' : 'No'} absent={ABSENT} />
                                <DetailField label="Created" value={day(claim.created)} absent={ABSENT} />
                            </>
                        )}
                    </S.Fields>
                    {claim && (
                        <S.Money>
                            {[
                                ['Charge', claim.chargeAmount],
                                ['Allowed', claim.allowedAmount],
                                ['Payer Paid', claim.payerAmount],
                                ['Patient Resp.', claim.patientAmount],
                            ].map(([label, amount]) => (
                                <div key={label as string}>
                                    <S.MoneyLabel>{label as string}</S.MoneyLabel>
                                    <S.MoneyValue>{money(amount as WorkflowClaim['chargeAmount']) || ABSENT}</S.MoneyValue>
                                </div>
                            ))}
                        </S.Money>
                    )}
                    {!!claim?.claimValidationErrors?.length && (
                        <Notice tone="warning">
                            {claim.claimValidationErrors.map(error => (
                                <div key={error}>{error}</div>
                            ))}
                        </Notice>
                    )}
                    <S.Actions>
                        <BaseButton
                            variant="primary"
                            size="compact"
                            onClick={(): void => {
                                window.open(claimHref(claimId), '_blank', 'noopener');
                            }}>
                            <Icon path={mdiOpenInNew} size={0.6} />
                            &nbsp;Open in New Tab
                        </BaseButton>
                        <BaseButton variant="outlined" size="compact" onClick={(): void => onCopy(claimId, 'Claim ID')}>
                            Copy Claim ID
                        </BaseButton>
                    </S.Actions>
                </S.Expanded>
            )}
        </>
    );
});

export interface IWorkflowDrawerProps {
    store: WorkerReportStore;
    /** The claim's page on this console. */
    claimHref: (claimId: string) => string;
}

/**
 * The claims behind one heatmap box (crosswalk §1, requests 2 and 3), one page of 100 at a time.
 *
 * The count here is the drawer's own: the drill-down is a separate snapshot from the page load, so it
 * can differ from the cell clicked, and the drawer says "as of" its own time rather than reconciling.
 */
export const WorkflowDrawer: React.FC<IWorkflowDrawerProps> = observer(({ store, claimHref }) => {
    const toast = React.useContext(ToastContext);
    const bodyRef = React.useRef<HTMLDivElement>(null);
    const box = store.box;
    const onCloseRef = React.useRef(store.closeDrawer);
    onCloseRef.current = store.closeDrawer;

    // Focus moves into the drawer, Escape closes it, focus returns — the denials drawer's behaviour.
    const boxKey = box ? `${box.status}|${box.week}|${box.action || ''}` : undefined;
    React.useEffect(() => {
        if (!boxKey) {
            return undefined;
        }
        const previous = document.activeElement as HTMLElement | null;
        bodyRef.current?.querySelector<HTMLElement>('button[aria-label="Close claims"]')?.focus();
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && !document.querySelector('.MuiDialog-root')) {
                onCloseRef.current();
            }
        };
        document.addEventListener('keydown', onKey);
        return (): void => {
            document.removeEventListener('keydown', onKey);
            previous?.focus?.();
        };
    }, [boxKey]);

    if (!box) {
        return null;
    }

    const copy = (text: string, what: string): void => {
        void navigator.clipboard
            ?.writeText(text)
            .then(() => toast.notify?.(`${what} copied`))
            .catch(() => toast.notify?.(`${what} could not be copied`));
    };

    const statusLabel = labelOf(store.catalog.statuses, box.status);
    const actionLabel = store.drawerAction ? labelOf(store.catalog.actions, store.drawerAction) : undefined;
    const description = store.drawerAction
        ? store.catalog.actions.get(store.drawerAction)?.description
        : store.catalog.statuses.get(box.status)?.description;
    const page = store.page;
    const pager = store.pager;
    const chips = store.chips;
    const chipTotal = chips.reduce((sum, chip) => sum + chip.count, 0);

    return (
        <SidePanel className="fixed below-topnav">
            <S.Body ref={bodyRef} role="dialog" aria-modal="false" aria-label="Claims in this box">
                <S.Header>
                    <S.TitleRow>
                        <div>
                            <S.Kicker>
                                {weekLabel(box.week)} · {weekRange(box.week)}
                            </S.Kicker>
                            <S.Title>{actionLabel ? `${statusLabel} / ${actionLabel}` : statusLabel}</S.Title>
                            <S.Meta>
                                {store.pageLoading && !page
                                    ? 'Loading claims…'
                                    : page
                                      ? `${page.total.toLocaleString()} claims${store.pageGeneratedAt ? ` · as of ${formatDate(store.pageGeneratedAt, 'h:mm a')}` : ''}`
                                      : ABSENT}
                            </S.Meta>
                            {description && description !== (actionLabel || statusLabel) && <S.Muted>{description}</S.Muted>}
                        </div>
                        <BaseButton variant="text" size="small" aria-label="Close claims" onClick={store.closeDrawer}>
                            <Icon path={mdiClose} size={0.8} />
                        </BaseButton>
                    </S.TitleRow>
                    {chips.length > 1 && (
                        <TypeFilterBar>
                            <TypeFilterChip active={!store.drawerAction} onClick={(): void => store.setDrawerAction(undefined)}>
                                All actions <TypeCountBadge active={!store.drawerAction}>{chipTotal.toLocaleString()}</TypeCountBadge>
                            </TypeFilterChip>
                            {chips.map(chip => (
                                <TypeFilterChip
                                    key={chip.action}
                                    active={store.drawerAction === chip.action}
                                    onClick={(): void => store.setDrawerAction(chip.action)}>
                                    {chip.label} <TypeCountBadge active={store.drawerAction === chip.action}>{chip.count.toLocaleString()}</TypeCountBadge>
                                </TypeFilterChip>
                            ))}
                        </TypeFilterBar>
                    )}
                    <S.Toolbar>
                        <BaseButton
                            variant="outlined"
                            size="compact"
                            disabled={!page?.claimIds.length}
                            onClick={(): void => page && copy(page.claimIds.join('\n'), `${page.claimIds.length} claim IDs on this page`)}>
                            <Icon path={mdiContentCopy} size={0.55} />
                            &nbsp;Copy claim IDs
                        </BaseButton>
                        {pager && (
                            <S.Pager>
                                {pager.from.toLocaleString()}–{pager.to.toLocaleString()} of {pager.total.toLocaleString()}
                                <BaseButton variant="outlined" size="compact" aria-label="Previous page" disabled={!pager.hasPrevious || store.pageLoading} onClick={store.previousPage}>
                                    <Icon path={mdiChevronLeft} size={0.6} />
                                </BaseButton>
                                <BaseButton variant="outlined" size="compact" aria-label="Next page" disabled={!pager.hasNext || store.pageLoading} onClick={store.nextPage}>
                                    <Icon path={mdiChevronRight} size={0.6} />
                                </BaseButton>
                            </S.Pager>
                        )}
                    </S.Toolbar>
                </S.Header>
                <S.ColumnHead>
                    <span />
                    <span>Claim ID / Patient</span>
                    <span>Payer / TPA</span>
                    <span>Charge / DOS</span>
                </S.ColumnHead>
                <S.Scroll>
                    {store.pageLoading ? (
                        <S.Center>
                            <Spinner size="16px" />
                            Loading claims. A box with many actions can take a few seconds.
                        </S.Center>
                    ) : store.pageError ? (
                        <S.Center>{store.pageError}</S.Center>
                    ) : page && !page.claimIds.length ? (
                        <S.Center>No claims in this box any more. Claims may have moved since the page loaded.</S.Center>
                    ) : (
                        <>
                            {store.claimsError && (
                                <Notice tone="attention">
                                    Claim details could not be loaded; the IDs and workflow state below are still accurate.
                                </Notice>
                            )}
                            {page?.claimIds.map(claimId => (
                                <ClaimRow
                                    key={claimId}
                                    store={store}
                                    claimId={claimId}
                                    claim={store.claims.get(claimId)}
                                    detail={store.details.get(claimId)}
                                    claimHref={claimHref}
                                    onCopy={copy}
                                />
                            ))}
                        </>
                    )}
                </S.Scroll>
            </S.Body>
        </SidePanel>
    );
});
